import { useRedisWritesBlocked } from '@/apis/call';
import { CallingStatusValues } from '@/constants/callingStatus';
import AgLoadingButton from '@components/AgLoadingButton';
import ButtonRow from '@components/ButtonRow';
import WarningChangeStatusDialog from '@components/WarningChangeStatusDialog';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ProfileNotDisplayingButton from './components/ProfileNotDisplayingButton';
import { useCallingTab } from './useCallingTab';

type CallingTabControlsProps = Pick<
  ReturnType<typeof useCallingTab>,
  | 'listCompleted'
  | 'callingViewContactIndex'
  | 'list'
  | 'debounceDisable'
  | 'previousIndex'
  | 'nextIndex'
  | 'handleNavigateBefore'
  | 'handleNavigateNext'
  | 'handleSkip'
  | 'handleSave'
  | 'callingStatus'
  | 'handleChangeStatus'
  | 'currentCallingStatus'
  | 'savingCallingStatus'
  | 'skippingCallingStatus'
  | 'setSavingCallingStatus'
  | 'setSkippingCallingStatus'
  | 'isWarningDialogOpen'
  | 'setIsWarningDialogOpen'
  | 'currentlyAssignedUser'
  | 'warningButtonName'
  | 'handleWarningDialogConfirm'
  | 'contactName'
  | 'contactId'
>;

export default function CallingTabControls({
  listCompleted,
  callingViewContactIndex,
  list,
  debounceDisable,
  previousIndex,
  nextIndex,
  handleNavigateBefore,
  handleNavigateNext,
  handleSkip,
  handleSave,
  callingStatus,
  handleChangeStatus,
  currentCallingStatus,
  savingCallingStatus,
  skippingCallingStatus,
  setSavingCallingStatus,
  setSkippingCallingStatus,
  isWarningDialogOpen,
  setIsWarningDialogOpen,
  currentlyAssignedUser,
  warningButtonName,
  handleWarningDialogConfirm,
  contactName,
  contactId,
}: CallingTabControlsProps) {
  const writesBlocked = useRedisWritesBlocked();
  return (
    <ButtonRow noMarginX sx={{ marginY: 0 }}>
      {!listCompleted && (
        <Box sx={{
          flexGrow: 1
        }}>
          <ProfileNotDisplayingButton contactName={contactName} contactId={contactId} />
        </Box>
      )}
      <Box>
        <AgLoadingButton
          variant="text"
          onClick={handleNavigateBefore}
          disabled={previousIndex === null || listCompleted || debounceDisable}
          loading={debounceDisable}
          preventMultipleClicks={false}
          aria-label="Previous contact"
        >
          <NavigateBeforeIcon fontSize="large" />
        </AgLoadingButton>
        <Typography variant="button">
          {callingViewContactIndex + 1} of {list?.count ?? 0}
        </Typography>
        <AgLoadingButton
          variant="text"
          onClick={() => handleNavigateNext(true)}
          disabled={nextIndex === null || listCompleted || debounceDisable}
          loading={debounceDisable}
          preventMultipleClicks={false}
          aria-label="Next contact"
        >
          <NavigateNextIcon fontSize="large" />
        </AgLoadingButton>
      </Box>

      <AgLoadingButton
        variant="outlined"
        onClick={async () => {
          setSkippingCallingStatus(true);
          try {
            await handleSkip();
          } finally {
            setSkippingCallingStatus(false);
          }
        }}
        disabled={
          writesBlocked ||
          currentCallingStatus !== CallingStatusValues[0] ||
          listCompleted ||
          savingCallingStatus ||
          debounceDisable
        }
        loading={debounceDisable}
        preventMultipleClicks={false}
      >
        Skip
      </AgLoadingButton>
      <Select
        label={<InputLabel>Calling status</InputLabel>}
        value={callingStatus}
        onChange={handleChangeStatus}
        variant="standard"
        sx={{ width: '220px', height: '56px' }}
        disabled={listCompleted || skippingCallingStatus || savingCallingStatus}
      >
        {CallingStatusValues.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>

      <AgLoadingButton
        variant="contained"
        onClick={async () => {
          setSavingCallingStatus(true);
          try {
            await handleSave();
          } finally {
            setSavingCallingStatus(false);
          }
        }}
        disabled={writesBlocked || listCompleted || skippingCallingStatus || debounceDisable}
        loading={debounceDisable}
        preventMultipleClicks={false}
      >
        {currentCallingStatus === CallingStatusValues[0] ? 'Save' : 'Update'}
      </AgLoadingButton>

      <WarningChangeStatusDialog
        isOpen={isWarningDialogOpen}
        onClose={() => {
          setIsWarningDialogOpen(false);
        }}
        currentlyAssignedUser={currentlyAssignedUser}
        buttonName={warningButtonName}
        handleUpdateCallingStatus={handleWarningDialogConfirm}
        confirmDisabled={writesBlocked}
      />
    </ButtonRow>
  );
}
